/**
 * Modal — full-screen centered dialog with optional buttons.
 *
 * Used by Pause, GameOver, settings popups, and the "Reconnecting…" overlay.
 * The backdrop intercepts pointer events so the game beneath cannot be
 * accidentally interacted with.
 */
import { Theme } from './Theme.js';
import { Button } from './Button.js';

export class Modal {
  /**
   * @param {object} opts
   * @param {Phaser.Scene} opts.scene
   * @param {string} opts.title
   * @param {string} [opts.message]
   * @param {Array<{label: string, onClick: () => void, variant?: string}>} [opts.buttons]
   */
  constructor(opts) {
    const scene = opts.scene;
    const w = scene.scale.width;
    const h = scene.scale.height;

    this.container = scene.add.container(0, 0).setDepth(3000);

    const dim = scene.add.rectangle(w / 2, h / 2, w, h, 0x000000, 0.6)
      .setInteractive(); // swallow clicks

    const panelW = Math.min(420, w - 40);
    const panelH = 220 + (opts.buttons ? opts.buttons.length * 60 : 0);
    const panelX = w / 2;
    const panelY = h / 2;

    const panel = scene.add.graphics();
    panel.fillStyle(Theme.colors.surface, 1);
    panel.fillRoundedRect(panelX - panelW / 2, panelY - panelH / 2, panelW, panelH, Theme.radius);
    panel.lineStyle(1, 0xffffff, 0.1);
    panel.strokeRoundedRect(panelX - panelW / 2, panelY - panelH / 2, panelW, panelH, Theme.radius);

    const title = scene.add.text(panelX, panelY - panelH / 2 + 36, opts.title, {
      fontFamily: Theme.font.family,
      fontSize: Theme.font.sizes.lg,
      color: Theme.colors.text,
      fontStyle: 'bold'
    }).setOrigin(0.5);

    this.container.add([dim, panel, title]);

    if (opts.message) {
      const message = scene.add.text(panelX, panelY - 30, opts.message, {
        fontFamily: Theme.font.family,
        fontSize: Theme.font.sizes.md,
        color: Theme.colors.textDim,
        align: 'center',
        wordWrap: { width: panelW - 40 }
      }).setOrigin(0.5);
      this.container.add(message);
    }

    if (opts.buttons && opts.buttons.length) {
      let by = panelY + 30;
      for (const b of opts.buttons) {
        const btn = new Button({
          scene,
          x: panelX,
          y: by,
          label: b.label,
          variant: b.variant || 'primary',
          onClick: () => {
            if (b.onClick) b.onClick();
          }
        });
        this.container.add(btn.container);
        by += 56;
      }
    }
  }

  close() {
    this.container.destroy();
  }
}
